import {
  Checkbox,
  cn,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useDateFnsFormattersWithLocale,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import type { HealthieAppoinmentType } from "@tellescope/tanstack-query";

export interface HealthieAppointmentTypeTableProps {
  appointmentTypes: HealthieAppoinmentType[];
  isLoading: boolean;
  isError: boolean;
  allAlreadyImported: boolean;
  selectedAppointmentTypeIds: string[];
  allSelected: boolean;
  hasSelection: boolean;
  onToggleAppointmentType: (id: string) => void;
  onToggleAll: (checked: boolean) => void;
}

export const HealthieAppointmentTypeTable = ({
  appointmentTypes,
  isLoading,
  isError,
  allAlreadyImported,
  selectedAppointmentTypeIds,
  allSelected,
  hasSelection,
  onToggleAppointmentType,
  onToggleAll,
}: HealthieAppointmentTypeTableProps) => {
  const { t } = useTranslation("onboarding");
  const { format } = useDateFnsFormattersWithLocale();

  return (
    <div className="flex max-h-full min-h-0 flex-col overflow-hidden rounded-md border">
      <Table className="table-fixed" containerClassName="min-h-0 overflow-y-auto">
        <TableHeader className="sticky top-0 z-10 bg-gray-50">
          <TableRow className="bg-gray-50 hover:bg-gray-50">
            <TableHead className="w-[65px] py-3 px-6">
              {isLoading ? (
                <Skeleton className="size-4 bg-gray-300" />
              ) : (
                <Checkbox
                  data-testid="onboarding-select-all-appointment-types"
                  checked={allSelected ? true : hasSelection ? "indeterminate" : false}
                  onCheckedChange={(checked) => onToggleAll(checked === true)}
                  aria-label={t("Select all appointment types")}
                />
              )}
            </TableHead>
            <TableHead className="py-4 px-6 uppercase text-xs text-muted-foreground">
              {isLoading ? <Skeleton className="h-3 w-32 bg-gray-300" /> : t("Appointment Type")}
            </TableHead>
            <TableHead className="w-40 py-4 px-6 uppercase text-xs text-muted-foreground">
              {isLoading ? <Skeleton className="h-3 w-24 bg-gray-300" /> : t("Created Date")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 8 }, (_, index) => (
              <TableRow key={index} className="bg-white">
                <TableCell className="py-4 px-6 w-[65px]">
                  <Skeleton className="size-4 bg-gray-300" />
                </TableCell>
                <TableCell className="py-4 px-6">
                  <Skeleton className="h-4 w-44 bg-gray-300" />
                </TableCell>
                <TableCell className="w-40 py-4 px-6">
                  <Skeleton className="h-4 w-24 bg-gray-300" />
                </TableCell>
              </TableRow>
            ))
          ) : !appointmentTypes.length ? (
            <TableRow className="bg-white hover:bg-white">
              <TableCell
                colSpan={3}
                className={cn("py-8 px-6 text-center text-sm", isError ? "text-destructive" : "text-muted-foreground")}
              >
                {isError
                  ? t("Failed to load appointment types from Healthie")
                  : allAlreadyImported
                  ? t("All Healthie appointment types have already been imported")
                  : t("No appointment types found in Healthie")}
              </TableCell>
            </TableRow>
          ) : (
            appointmentTypes.map(({ id, name, created_at }) => (
              <TableRow
                key={id}
                data-testid={`onboarding-appointment-type-row-${id}`}
                className="bg-white"
                role="button"
                onClick={() => onToggleAppointmentType(id)}
              >
                <TableCell className="py-4 px-6 w-[65px]">
                  <Checkbox
                    checked={selectedAppointmentTypeIds.includes(id)}
                    onCheckedChange={() => onToggleAppointmentType(id)}
                    aria-label={t("Select {{name}}", { name })}
                  />
                </TableCell>
                <TableCell className="py-4 px-6 truncate">{name}</TableCell>
                <TableCell className="w-40 py-4 px-6 text-muted-foreground">
                  {format(created_at, "MMM d, yyyy")}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
